import { useEffect, useRef } from 'react';

/**
 * FAZ 1 / F1-17 — Sekme gizliyken polling'i durduran interval hook'u.
 *
 * ── Cozulen problem (rapor §11.5) ─────────────────────────────────────────
 * Uygulamada real-time yok; frontend polling yapiyor. B2B paneli acik tek bir
 * kullanici, HICBIR SEY YAPMASA BILE ~0,20 istek/s uretiyordu:
 *
 *     1000 kullanici →  200 istek/s   |   2000 kullanici →  400 istek/s
 *
 * Bu, etkilesim olmadan gelen TABAN yuktu. Daha kotusu: `setInterval`
 * tarayici sekmesi arka planda olsa bile calismaya devam eder. Fuar ortaminda
 * kullanicilar paneli acik birakip baska sekmeye gecer — sunucu bu "izlenmeyen"
 * sekmelere de yanit uretmeye devam ediyordu.
 *
 * ── Ne yapiyor ────────────────────────────────────────────────────────────
 * `document.visibilityState` dinlenir; sekme gizlendiginde interval TAMAMEN
 * durdurulur (yavaslatilmaz), geri gelindiginde yeniden kurulur.
 *
 * Tarayicilar arka plan sekmelerinde `setInterval`'i zaten ~1 dakikaya kadar
 * kisitlar (throttling) — ama DURDURMAZ. Ustelik bu davranis tarayiciya gore
 * degisir ve arka planda ses calan sekmelerde hic uygulanmaz. Acik durdurma
 * tek guvenilir yol.
 *
 * ── Ilk cagri bu hook'un isi DEGIL ────────────────────────────────────────
 * Bilesenler ilk yuklemeyi genelde farkli parametrelerle yapar
 * (ornegin `loadParticipants(true)` — yukleme gostergesiyle). Bu yuzden hook
 * mount aninda cagri YAPMAZ; yalnizca interval'i ve gorunurlugu yonetir.
 * Ilk cagriyi bilesen kendisi yapmaya devam eder.
 *
 * ── Sekmeye donuste tazeleme ──────────────────────────────────────────────
 * Sekme gizliyken veri bayatlar; geri donuldugunde bir sonraki interval'i
 * beklemek kotu bir deneyimdir. `runOnResume` (varsayilan acik) donuste hemen
 * bir cagri yapar.
 *
 * ⚠️ Ancak hizli sekme degistirmede (alt-tab) bu, polling'den DAHA COK istek
 * uretebilirdi. Bu yuzden donusteki tazeleme yalnizca son cagrinin uzerinden
 * en az `intervalMs` gectiyse yapilir.
 */
export type VisiblePollingOptions = {
  /** `false` iken interval hic kurulmaz (ornegin secili etkinlik yokken). */
  enabled?: boolean;
  /** Sekmeye donuldugunde hemen bir kez calistir. Varsayilan: true. */
  runOnResume?: boolean;
};

export function useVisiblePolling(
  callback: () => void,
  intervalMs: number,
  options: VisiblePollingOptions = {},
): void {
  const { enabled = true, runOnResume = true } = options;

  // Callback her render'da degisebilir; interval'i yeniden kurmamak icin
  // ref uzerinden HER ZAMAN en gunceli cagrilir.
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  const lastRunAtRef = useRef(0);

  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0) return undefined;

    let intervalId: number | undefined;

    const isVisible = () =>
      typeof document === 'undefined' || document.visibilityState === 'visible';

    const run = () => {
      lastRunAtRef.current = Date.now();
      savedCallback.current();
    };

    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const start = () => {
      if (intervalId !== undefined) return;
      intervalId = window.setInterval(run, intervalMs);
    };

    const handleVisibilityChange = () => {
      if (!isVisible()) {
        stop();
        return;
      }
      // Hizli sekme degisiminde istek firtinasi olusmasin diye
      // yalnizca veri gercekten bayatladiysa tazele.
      if (runOnResume && Date.now() - lastRunAtRef.current >= intervalMs) {
        run();
      }
      start();
    };

    if (isVisible()) start();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, intervalMs, runOnResume]);
}

export default useVisiblePolling;
